import React from "react";
import { Button, Header, Card } from "semantic-ui-react";
import { formatPhoneNumber } from "../../../../utils/FormatPhoneNumber";
import { ContactAvatar } from "../ContactAvatar";
import ContactEllipsesButtonDropdown from "./ContactEllipsesButtonDropdown";
import { PermitsContact } from "../../types/PermitsContact";
import { useLocation } from "react-router-dom";
import { ContactInfoOptions } from "../../types/ContactInfoOptions";
import {
  faBriefcase,
  faEnvelope,
  faIdBadge,
  faLocationDot,
  faMailboxFlagUp,
  faPhone,
} from "@fortawesome/pro-regular-svg-icons";
import EditInfoContactSidebar from "../staff/review/contact_information/EditInfoContactSidebar";
import useCountries from "../../../../hooks/useCountries";
import { FullAddress } from "../../../../utils/FullAddress";
import { FullPhysicalAddress } from "../../../../utils/FullPhysicalAddress";
import { ContactCardDetails } from "./ContactCardDetails";

interface IContactCardProps {
  contact: PermitsContact;
  showEditButton?: boolean;
  contactInfoOptions: ContactInfoOptions;
  permitsExtensionId?: number; // id of this contact's form field so we can update it
  customFieldType?: number;
  contactCanBeDeleted?: boolean;
  customFieldName?: string;
  isPrintView?: boolean;
  isPublicView?: boolean;
}

/**
 * Display a contact card with contact information
 */
export const ContactCard = (props: IContactCardProps) => {
  const { agencyCountry } = useCountries();
  const {
    contact,
    showEditButton = false,
    contactInfoOptions,
    permitsExtensionId = null,
    customFieldType = 10,
    contactCanBeDeleted = false,
    customFieldName,
    isPrintView,
    isPublicView,
  } = props;

  const hasAnyAddressInfo = !!(
    contact?.address ||
    contact?.address2 ||
    contact?.city ||
    contact?.state ||
    contact?.zip
  );

  const showCountryFlag = !!agencyCountry && contact?.countryId !== agencyCountry?.id;
  const showHomeCountryFlag = !!agencyCountry && contact?.homeCountryId !== agencyCountry?.id;

  const physicalAddress = (
    <FullPhysicalAddress
      address={contact?.physicalAddress}
      address2={contact?.physicalAddress2}
      city={contact?.physicalCity}
      state={contact?.physicalState}
      zip={contact?.physicalZip}
      hasAddress={!!contact?.address}
      sameMailAndPhysicalAddress={contact?.sameMailAndPhysicalAddress}
      countryId={showHomeCountryFlag ? contact?.homeCountryId : null}
    />
  );
  const isConnected = !!contact?.userId;
  const showAvatar = isConnected && !isPrintView;
  const contactType = contact?.contactType || "Contact";
  const contactDoesntExist = contact?.id == null;

  // IsCitizen prop exists, but was not getting expected results. This is a workaround to check if its public side
  const location = useLocation();
  const [showEditContactSidebar, setShowEditContactSidebar] = React.useState(false);

  return (
    <>
      <EditInfoContactSidebar
        contact={contact}
        showEditContactSidebar={showEditContactSidebar}
        setShowEditContactSidebar={setShowEditContactSidebar}
        customFieldType={customFieldType}
        contactInfoOptions={contactInfoOptions}
        contactDoesntExist={contactDoesntExist}
        customFieldName={customFieldName}
        permitsExtensionDefId={permitsExtensionId}
      />
      <div className='additional-contact-card-field'>
        {contact && (
          <Card className='contact-card-field'>
            <Card.Content>
              <div className='tw-mb-2 tw-mt-1 tw-flex tw-justify-between tw-gap-2 tw-align-middle'>
                <div className='tw-flex tw-gap-2'>
                  {showAvatar && !location.pathname.includes("public") && (
                    <ContactAvatar contact={contact} linkToConnectedCitizen />
                  )}
                  <div className='tw-flex tw-flex-col'>
                    <Header sub className='secondary-text'>
                      {contactType.toUpperCase()}
                    </Header>
                    <Header as='h4' className='tw-m-0 tw-p-0'>
                      {contact?.firstName} {contact?.lastName}
                    </Header>
                  </div>
                </div>

                {showEditButton && (
                  <div className='tw-absolute tw-right-1'>
                    <ContactEllipsesButtonDropdown
                      contact={contact}
                      contactInfoOptions={contactInfoOptions}
                      customFieldType={customFieldType}
                      permitsExtensionDefId={permitsExtensionId}
                      disableDeleteButton={!contactCanBeDeleted}
                      contactDoesntExist={contactDoesntExist}
                      customFieldName={customFieldName}
                    />
                  </div>
                )}
              </div>

              <Card.Description className='tw-ml-2 tw-flex tw-flex-col tw-gap-y-2'>
                {contact?.email && (
                  <ContactCardDetails
                    className='tw-break-all'
                    fieldName='Email'
                    fieldValue={<p>{contact?.email}</p>}
                    icon={faEnvelope}
                  />
                )}
                {contact?.phone && (
                  <ContactCardDetails
                    fieldName='Phone Number'
                    fieldValue={<p>{formatPhoneNumber(contact?.phone)}</p>}
                    icon={faPhone}
                  />
                )}
                {hasAnyAddressInfo && (
                  <ContactCardDetails
                    fieldName='Mailing Address'
                    fieldValue={
                      <div className='field-row address'>
                        <div className='address-field'>
                          <p>
                            <FullAddress
                              address={contact.address}
                              address2={contact.address2}
                              city={contact.city}
                              state={contact.state}
                              zip={contact.zip}
                              attn={null}
                              countryId={showCountryFlag ? contact?.countryId : null}
                            />
                          </p>
                        </div>
                      </div>
                    }
                    icon={faMailboxFlagUp}
                  />
                )}
                {!!physicalAddress && (
                  <ContactCardDetails
                    fieldName='Physical Address'
                    fieldValue={
                      <FullPhysicalAddress
                        address={contact?.physicalAddress}
                        address2={contact?.physicalAddress2}
                        city={contact?.physicalCity}
                        state={contact?.physicalState}
                        zip={contact?.physicalZip}
                        hasAddress={!!contact?.address}
                        sameMailAndPhysicalAddress={contact?.sameMailAndPhysicalAddress}
                        countryId={showHomeCountryFlag ? contact?.homeCountryId : null}
                      />
                    }
                    icon={faLocationDot}
                  />
                )}
                {contact?.companyName && (
                  <ContactCardDetails
                    fieldName='Company Name'
                    fieldValue={<p>{contact?.companyName}</p>}
                    icon={faBriefcase}
                  />
                )}
                {contact?.licenseNumber && (
                  <ContactCardDetails
                    fieldName='License Number'
                    fieldValue={<p>{contact?.licenseNumber}</p>}
                    icon={faIdBadge}
                  />
                )}
              </Card.Description>
            </Card.Content>
          </Card>
        )}
        {!contact && (
          <>
            <span> N/A </span>
            {!isPublicView && (
              <Button
                size='small'
                onClick={() => setShowEditContactSidebar(true)}
                className='no-padding-button'
                content='Add Contact'
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

/**
 * Display contact cards for an Additional Contacts field on a permit
 */
export const AdditionalContactCards = (props: any) => {
  const { contacts, showEditButton, contactInfoOptions, permitsExtensionId } = props;
  return (
    <div className='additional-contact-card-field'>
      {contacts.map((contactInfo: any, index: number) => {
        return (
          <ContactCard
            key={index}
            showEditButton={showEditButton}
            contact={contactInfo}
            contactInfoOptions={contactInfoOptions}
            customFieldType={11}
            permitsExtensionId={permitsExtensionId}
            contactCanBeDeleted={true}
          />
        );
      })}
    </div>
  );
};
